import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Check, Ban } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function reasonLabel(r) {
  switch (r) {
    case "ok": return "Unlocked";
    case "inactive": return "Disabled";
    case "expired": return "Expired";
    case "max_uses_reached": return "Usage limit";
    case "no_file": return "No file attached";
    case "file_missing": return "File missing";
    default: return r || "—";
  }
}

export default function LogsModal({ code, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/admin/codes/${code.id}/logs`)
      .then((r) => { if (!cancelled) setLogs(r.data); })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [code.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)" }}
      data-testid="logs-modal"
      onClick={onClose}
    >
      <div
        className="glass rounded-2xl p-8 max-w-3xl w-full fade-up max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="overline text-[var(--vault-text-muted)]">Access Log</p>
            <h3 className="font-display text-2xl mt-1">{code.label || code.code}</h3>
            <p className="text-sm text-[var(--vault-text-muted)] mt-1 font-mono-vault">{code.code}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5" data-testid="logs-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-auto rounded-lg border" style={{ borderColor: "var(--vault-border)" }}>
          <table className="w-full text-sm">
            <thead className="sticky top-0" style={{ background: "var(--vault-surface)" }}>
              <tr className="text-left overline text-[var(--vault-text-muted)]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Result</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">Device</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-4 py-10 text-center text-[var(--vault-text-muted)]">Loading…</td></tr>
              )}
              {!loading && logs.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-[var(--vault-text-muted)]">No accesses yet.</td></tr>
              )}
              {!loading && logs.map((l) => (
                <tr key={l.id} className="row border-t" style={{ borderColor: "var(--vault-border)" }}>
                  <td className="px-4 py-3 text-xs text-[var(--vault-text-muted)]">{fmtDate(l.timestamp)}</td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full"
                      style={{
                        background: l.success ? "rgba(22, 101, 52, 0.15)" : "rgba(153, 27, 27, 0.15)",
                        color: l.success ? "#86EFAC" : "#FCA5A5",
                      }}
                    >
                      {l.success ? <Check className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                      {reasonLabel(l.reason)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono-vault text-xs text-[var(--vault-text-muted)]">{l.ip || "—"}</td>
                  <td className="px-4 py-3 text-xs text-[var(--vault-text-muted)] truncate max-w-xs" title={l.user_agent || ""}>{l.user_agent || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
